import { RiEditLine, RiUserAddLine } from '@remixicon/react';
import { initials } from '../utils/format';

const AVATAR_COLORS = [
  'bg-amber-100 text-amber-900',
  'bg-blue-100 text-blue-900',
  'bg-purple-100 text-purple-900',
  'bg-rose-100 text-rose-900',
  'bg-emerald-100 text-emerald-900',
  'bg-indigo-100 text-indigo-900',
];

const avatarColorFor = (key = '') => {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const formatRole = (role) => {
  if (!role) return '';
  const map = {
    admin: 'Admin',
    ADMIN: 'Admin',
    broker: 'Broker',
    junior_broker: 'Junior Broker',
    finance: 'Finance / Accounting',
  };
  return map[role] || role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const TeamMemberRow = ({ member, isYou, onEdit }) => {
  const first = member.first_name || '';
  const last = member.last_name || '';
  const fullName = [first, last].filter(Boolean).join(' ') || member.email || 'Member';
  const colorClass = avatarColorFor(member.id || member.email || fullName);
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${colorClass}`}
      >
        {initials(first, last) || (member.email || 'U').charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">
          {fullName}
          {isYou && <span className="font-normal text-gray-500"> (You)</span>}
        </p>
        <p className="text-xs text-gray-500 truncate mt-0.5">{formatRole(member.role)}</p>
      </div>
      {member.role !== 'admin' && member.role !== 'ADMIN' && (
        <button
          type="button"
          onClick={() => onEdit?.(member)}
          className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label={`Edit ${fullName}`}
        >
          <RiEditLine className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const TeamMembersPanel = ({ members = [], currentUserId, onEditMember, onAddMember }) => {
  return (
    <div className="flex flex-col gap-6 min-h-[calc(100vh-140px)] bg-white/70 backdrop-blur-sm rounded-[2.5rem] pb-4">
      <div className="p-4 md:p-6 space-y-4">
        <p className="text-sm text-gray-700 px-1">
          Invite team members to work with and assign member permissions.
        </p>

        <div className="bg-white rounded-2xl divide-y divide-gray-100">
          {members.length === 0 ? (
            <div className="px-4 py-6 text-sm text-gray-500 text-center">
              No team members yet.
            </div>
          ) : (
            members.map((m) => (
              <TeamMemberRow
                key={m.id || m.email}
                member={m}
                isYou={m.id === currentUserId}
                onEdit={onEditMember}
              />
            ))
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={onAddMember}
        className="mt-auto w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-indigo-50 text-sm font-medium text-primary-700 hover:bg-indigo-100 transition-colors"
      >
        <RiUserAddLine className="w-4 h-4" />
        Add member
      </button>
    </div>
  );
};

export default TeamMembersPanel;
